import React, { useState, useEffect, useCallback } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import './App.css';
import TeamCityAPI from './services/teamcityApi';
import BuildServers from './components/BuildServers';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Settings from './components/Settings';
import Health from './components/Health';
import RunBuild from './components/RunBuild';
import BuildHistory from './components/BuildHistory';
import OrkaDashboard from './components/orka/OrkaDashboard';
import TestFlight from './components/testflight/TestFlight';
import Pokeball from './components/Pokeball';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Top-bar navigation - persistent across all pages. adminOnly entries are
// hidden (and their routes redirect home) for non-admin users.
const NAV_ITEMS = [
  { to: '/orka', label: 'Orka', adminOnly: true },
  { to: '/run-build', label: 'Run Build', adminOnly: true },
  { to: '/my-builds', label: 'My Builds', adminOnly: true },
  { to: '/health', label: 'Health' },
  { to: '/testflight', label: 'TestFlight', adminOnly: true },
  { to: '/settings', label: 'Settings' },
];

const ORKA_TABS = ['nodes', 'vms', 'images'];

// Route wrapper: maps /orka/:tab to the Orka dashboard's active tab and back.
function OrkaRoute() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = ORKA_TABS.includes(tab) ? tab : 'nodes';
  return (
    <OrkaDashboard
      activeTab={active}
      onTabChange={(key) => navigate(`/orka/${key}`)}
      onBack={() => navigate('/')}
    />
  );
}

// Route wrapper: a re-run navigates here with the snapshot in router state, which
// becomes RunBuild's initialSettings. A plain nav (no state) opens a fresh form.
function RunBuildRoute(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const prefill = location.state?.prefill || null;
  return <RunBuild {...props} initialSettings={prefill} onBack={() => navigate('/')} />;
}

function AppContent() {
  const [buildConfigs, setBuildConfigs] = useState([]);
  const [showPersonalBuilds, setShowPersonalBuilds] = useState(false);
  const [enableDebugging, setEnableDebugging] = useState(false);
  const [showQueuedBuilds, setShowQueuedBuilds] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    // Check for saved preference, otherwise default to light mode
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return saved === 'true';
    return false;
  });
  const [serverStatus, setServerStatus] = useState({ checking: true, ok: false, message: '' });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const { currentUser, isAdmin, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Shared notification state - tracks which builds the user wants to be notified about
  const [watchedBuilds, setWatchedBuilds] = useState(() => {
    const saved = localStorage.getItem('watchedBuilds');
    return saved ? JSON.parse(saved) : {};
  });

  // Save watched builds to localStorage
  useEffect(() => {
    localStorage.setItem('watchedBuilds', JSON.stringify(watchedBuilds));
  }, [watchedBuilds]);

  // Apply dark mode class to document
  useEffect(() => {
    document.documentElement.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Create TeamCityAPI with token getter
  const [teamcityApi] = useState(() => new TeamCityAPI());

  // Update token getter when getToken changes
  useEffect(() => {
    teamcityApi.setTokenGetter(getToken);
  }, [teamcityApi, getToken]);

  const checkServerHealth = useCallback(async () => {
    try {
      const health = await teamcityApi.checkHealth();
      setServerStatus({
        checking: false,
        ok: health.status === 'ok',
        message: health.teamcityConfigured
          ? 'Connected to backend server'
          : 'Backend server running but TeamCity not configured'
      });
      return health.status === 'ok';
    } catch (error) {
      setServerStatus({
        checking: false,
        ok: false,
        message: `Cannot connect to backend server. Make sure it is running on port ${process.env.REACT_APP_API_URL?.split(':')[2]?.split('/')[0] || '3151'}.`
      });
      return false;
    }
  }, [teamcityApi]);

  const loadUserPreferences = useCallback(async () => {
    try {
      const prefs = await teamcityApi.getUserPreferences();
      setBuildConfigs(prefs.buildConfigs || []);
      setShowPersonalBuilds(prefs.showPersonalBuilds !== undefined ? prefs.showPersonalBuilds : false);
      setEnableDebugging(prefs.enableDebugging !== undefined ? prefs.enableDebugging : false);
      setShowQueuedBuilds(prefs.showQueuedBuilds !== undefined ? prefs.showQueuedBuilds : true);
      setPrefsLoaded(true);
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      setPrefsLoaded(true);
    }
  }, [teamcityApi]);

  // Check server health and load user preferences on mount
  useEffect(() => {
    if (currentUser) {
      checkServerHealth().then(ok => {
        if (ok) {
          loadUserPreferences();
        }
      });
    }
  }, [checkServerHealth, loadUserPreferences, currentUser]);

  function handleSettingsSave(newConfigs, newShowPersonalBuilds, newEnableDebugging, newShowQueuedBuilds) {
    setBuildConfigs(newConfigs);
    if (newShowPersonalBuilds !== undefined) {
      setShowPersonalBuilds(newShowPersonalBuilds);
    }
    if (newEnableDebugging !== undefined) {
      setEnableDebugging(newEnableDebugging);
    }
    if (newShowQueuedBuilds !== undefined) {
      setShowQueuedBuilds(newShowQueuedBuilds);
    }
    // Don't navigate away - let user make multiple edits
  }

  async function handleReorder(newOrder) {
    setBuildConfigs(newOrder);
    try {
      await teamcityApi.saveUserPreferences(newOrder, showPersonalBuilds, enableDebugging, showQueuedBuilds);
    } catch (error) {
      console.error('Failed to save reordered builds:', error);
    }
  }

  // Re-run from My Builds: navigate to Run Build carrying the snapshot in router state.
  function handleRerun(snapshot) {
    navigate('/run-build', { state: { prefill: snapshot } });
  }

  // Show login if not authenticated
  if (!currentUser) {
    return (
      <div className="App">
        <header className="App-header">
          <h1><Pokeball size={28} className="header-pokeball" /> The Pokédex</h1>
        </header>
        <main className="App-main">
          <Login />
        </main>
      </div>
    );
  }

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  // Dashboard ("/") content, with its own loading / empty-state gates.
  const dashboardElement = !prefsLoaded ? (
    <div className="status-message checking">
      <p>Loading your preferences...</p>
    </div>
  ) : buildConfigs.length === 0 ? (
    <div className="status-message">
      <h2>No Build Configurations Selected</h2>
      <p>You haven't selected any build configurations to monitor yet.</p>
      <button onClick={() => navigate('/settings')} className="retry-button">
        Go to Settings
      </button>
    </div>
  ) : (
    <>
      <BuildServers
        teamcityApi={teamcityApi}
        enableDebugging={enableDebugging}
        showQueuedBuilds={showQueuedBuilds}
        watchedBuilds={watchedBuilds}
        setWatchedBuilds={setWatchedBuilds}
      />
      <Dashboard
        teamcityApi={teamcityApi}
        buildConfigs={buildConfigs}
        showPersonalBuilds={showPersonalBuilds}
        enableDebugging={enableDebugging}
        onReorder={handleReorder}
        watchedBuilds={watchedBuilds}
        setWatchedBuilds={setWatchedBuilds}
      />
    </>
  );

  return (
    <div className="App">
      <header className="App-header">
        <h1>
          <Link to="/" className="header-home" title="Go to dashboard">
            <Pokeball size={28} className="header-pokeball" /> The Pokédex
          </Link>
        </h1>
        <div className="user-info">
          <span>{currentUser.email}</span>
          {NAV_ITEMS.filter(({ adminOnly }) => !adminOnly || isAdmin).map(({ to, label }) => (
            <button
              key={to}
              onClick={() => navigate(to)}
              className={`settings-button ${isActive(to) ? 'active' : ''}`}
            >
              {label}
            </button>
          ))}
          <button onClick={logout} className="logout-button">Log Out</button>
        </div>
      </header>

      <main className="App-main">
        {serverStatus.checking ? (
          <div className="status-message checking">
            <p>Checking server connection...</p>
          </div>
        ) : !serverStatus.ok ? (
          <div className="status-message error">
            <h2>Backend Server Not Available</h2>
            <p>{serverStatus.message}</p>
            <p>Please start the backend server by running:</p>
            <code>npm run server</code>
            <button onClick={checkServerHealth} className="retry-button">
              Retry Connection
            </button>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={dashboardElement} />
            <Route
              path="/orka"
              element={isAdmin ? <Navigate to="/orka/nodes" replace /> : <Navigate to="/" replace />}
            />
            <Route
              path="/orka/:tab"
              element={isAdmin ? <OrkaRoute /> : <Navigate to="/" replace />}
            />
            <Route
              path="/run-build"
              element={
                isAdmin ? (
                  <RunBuildRoute
                    teamcityApi={teamcityApi}
                    buildConfigs={buildConfigs}
                    isAdmin={isAdmin}
                    watchedBuilds={watchedBuilds}
                    setWatchedBuilds={setWatchedBuilds}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/my-builds"
              element={
                isAdmin ? (
                  <BuildHistory
                    teamcityApi={teamcityApi}
                    onRerun={handleRerun}
                    onBack={() => navigate('/')}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/health"
              element={<Health teamcityApi={teamcityApi} onBack={() => navigate('/')} />}
            />
            <Route
              path="/testflight"
              element={isAdmin ? <TestFlight /> : <Navigate to="/" replace />}
            />
            <Route
              path="/settings"
              element={
                <Settings
                  teamcityApi={teamcityApi}
                  selectedBuilds={buildConfigs}
                  showPersonalBuilds={showPersonalBuilds}
                  enableDebugging={enableDebugging}
                  showQueuedBuilds={showQueuedBuilds}
                  darkMode={darkMode}
                  onDarkModeChange={setDarkMode}
                  onSave={handleSettingsSave}
                  onBack={() => navigate('/')}
                />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
